/**
 * CHORUS STORE - Reactive State Management
 * Pub/Sub pattern with fine-grained subscriptions
 */

/**
 * Creates a reactive store with pub/sub capabilities
 * @template T
 * @param {T} initialState - The initial state object
 * @returns {Object} Store API with getState, setState, subscribe, etc.
 */
export function createStore(initialState) {
  let state = structuredClone(initialState);
  const listeners = new Set();
  const pathListeners = new Map();

  /**
   * Get the current state (immutable copy)
   * @returns {T}
   */
  function getState() {
    return structuredClone(state);
  }

  /**
   * Get a value at a specific path
   * @param {string} path - Dot-notation path (e.g., 'user.settings.theme')
   * @returns {*}
   */
  function getPath(path) {
    return path.split('.').reduce((obj, key) => obj?.[key], state);
  }

  /**
   * Set state with partial updates
   * @param {Partial<T> | ((state: T) => Partial<T>)} update - Partial state or updater function
   */
  function setState(update) {
    const prevState = state;
    const changes = typeof update === 'function' ? update(state) : update;

    state = deepMerge(structuredClone(state), changes);

    // Calculate changed paths
    const changedPaths = getChangedPaths(prevState, state);

    // Notify path-specific listeners
    changedPaths.forEach(path => {
      const pathCallbacks = pathListeners.get(path);
      if (pathCallbacks) {
        const newValue = getPath(path);
        const oldValue = path.split('.').reduce((obj, key) => obj?.[key], prevState);
        pathCallbacks.forEach(callback => callback(newValue, oldValue, path));
      }

      // Notify parent path listeners
      const parts = path.split('.');
      for (let i = 1; i < parts.length; i++) {
        const parentPath = parts.slice(0, i).join('.');
        const parentCallbacks = pathListeners.get(parentPath);
        if (parentCallbacks) {
          const newValue = getPath(parentPath);
          const oldValue = parts.slice(0, i).reduce((obj, key) => obj?.[key], prevState);
          parentCallbacks.forEach(callback => callback(newValue, oldValue, parentPath));
        }
      }
    });

    // Notify global listeners
    listeners.forEach(callback => callback(state, prevState, changedPaths));
  }

  /**
   * Set a value at a specific path
   * @param {string} path - Dot-notation path
   * @param {*} value - New value
   */
  function setPath(path, value) {
    const keys = path.split('.');
    const update = keys.reduceRight((acc, key) => ({ [key]: acc }), value);
    setState(update);
  }

  /**
   * Subscribe to all state changes
   * @param {(state: T, prevState: T, changedPaths: string[]) => void} callback
   * @returns {() => void} Unsubscribe function
   */
  function subscribe(callback) {
    listeners.add(callback);
    return () => listeners.delete(callback);
  }

  /**
   * Subscribe to changes at a specific path
   * @param {string} path - Dot-notation path
   * @param {(newValue: *, oldValue: *, path: string) => void} callback
   * @returns {() => void} Unsubscribe function
   */
  function subscribePath(path, callback) {
    if (!pathListeners.has(path)) {
      pathListeners.set(path, new Set());
    }
    pathListeners.get(path).add(callback);
    return () => pathListeners.get(path)?.delete(callback);
  }

  /**
   * Reset state to initial values
   */
  function reset() {
    setState(structuredClone(initialState));
  }

  /**
   * Create a derived/computed value
   * @param {(state: T) => *} selector - Function to derive value from state
   * @param {(value: *) => void} callback - Called when derived value changes
   * @returns {() => void} Unsubscribe function
   */
  function derived(selector, callback) {
    let prevValue = selector(state);
    callback(prevValue);

    return subscribe((newState) => {
      const newValue = selector(newState);
      if (!deepEqual(prevValue, newValue)) {
        prevValue = newValue;
        callback(newValue);
      }
    });
  }

  /**
   * Batch multiple updates into a single notification
   * @param {() => void} fn - Function containing multiple setState calls
   */
  function batch(fn) {
    const prevState = state;
    const prevListeners = new Set(listeners);
    const prevPathListeners = new Map(pathListeners);

    // Temporarily replace listeners
    listeners.clear();
    pathListeners.clear();

    // Execute batch updates
    fn();

    // Restore listeners
    prevListeners.forEach(l => listeners.add(l));
    prevPathListeners.forEach((v, k) => pathListeners.set(k, v));

    // Single notification with all changes
    const changedPaths = getChangedPaths(prevState, state);
    if (changedPaths.length > 0) {
      listeners.forEach(callback => callback(state, prevState, changedPaths));

      changedPaths.forEach(path => {
        const pathCallbacks = pathListeners.get(path);
        if (pathCallbacks) {
          const newValue = getPath(path);
          const oldValue = path.split('.').reduce((obj, key) => obj?.[key], prevState);
          pathCallbacks.forEach(callback => callback(newValue, oldValue, path));
        }
      });
    }
  }

  return {
    getState,
    getPath,
    setState,
    setPath,
    subscribe,
    subscribePath,
    derived,
    batch,
    reset,
  };
}

/**
 * Deep merge two objects
 * @param {Object} target
 * @param {Object} source
 * @returns {Object}
 */
function deepMerge(target, source) {
  if (source === null || source === undefined) {
    return target;
  }

  if (typeof source !== 'object' || Array.isArray(source)) {
    return source;
  }

  const result = { ...target };

  for (const key of Object.keys(source)) {
    if (
      typeof source[key] === 'object' &&
      source[key] !== null &&
      !Array.isArray(source[key]) &&
      typeof target[key] === 'object' &&
      target[key] !== null &&
      !Array.isArray(target[key])
    ) {
      result[key] = deepMerge(target[key], source[key]);
    } else {
      result[key] = source[key];
    }
  }

  return result;
}

/**
 * Deep equality check
 * @param {*} a
 * @param {*} b
 * @returns {boolean}
 */
function deepEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;

  if (typeof a !== 'object') return false;

  if (Array.isArray(a) !== Array.isArray(b)) return false;

  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    return a.every((item, i) => deepEqual(item, b[i]));
  }

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  return keysA.every(key => deepEqual(a[key], b[key]));
}

/**
 * Get changed paths between two state objects
 * @param {Object} prev
 * @param {Object} next
 * @param {string} prefix
 * @returns {string[]}
 */
function getChangedPaths(prev, next, prefix = '') {
  const paths = [];
  const allKeys = new Set([...Object.keys(prev || {}), ...Object.keys(next || {})]);

  for (const key of allKeys) {
    const path = prefix ? `${prefix}.${key}` : key;
    const prevVal = prev?.[key];
    const nextVal = next?.[key];

    if (!deepEqual(prevVal, nextVal)) {
      paths.push(path);

      if (
        typeof prevVal === 'object' &&
        typeof nextVal === 'object' &&
        prevVal !== null &&
        nextVal !== null &&
        !Array.isArray(prevVal) &&
        !Array.isArray(nextVal)
      ) {
        paths.push(...getChangedPaths(prevVal, nextVal, path));
      }
    }
  }

  return paths;
}

/**
 * Create an action that modifies the store
 * @template T
 * @param {Object} store - Store instance
 * @param {string} name - Action name for debugging
 * @param {(state: T, ...args: any[]) => Partial<T>} reducer - Reducer function
 * @returns {(...args: any[]) => void}
 */
export function createAction(store, name, reducer) {
  return (...args) => {
    const changes = reducer(store.getState(), ...args);
    store.setState(changes);
  };
}

/**
 * Create a selector with memoization
 * @template T, R
 * @param {(state: T) => R} selector
 * @returns {(state: T) => R}
 */
export function createSelector(selector) {
  let lastState = null;
  let lastResult = null;

  return (state) => {
    if (state !== lastState) {
      lastState = state;
      lastResult = selector(state);
    }
    return lastResult;
  };
}

export default createStore;
